import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, X, ExternalLink, Copy, Send, ArrowLeft, EyeOff, Eye,
  ChevronRight, FileText, Users, Layers,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  new: "New",
  under_review: "Under Review",
  shortlisted: "Shortlisted",
  test_assigned: "Test Assigned",
  test_submitted: "Test Submitted",
  test_evaluation: "Test Evaluation",
  interview: "Interview",
  selected: "Selected",
  talent_pool: "Talent Pool",
  rejected: "Rejected",
  onboarded: "Onboarded",
};
const STATUS_KEYS = Object.keys(STATUS_LABEL);

const fmtDate = (v) => {
  if (!v) return "—";
  const d = typeof v === "string" ? new Date(v) : v;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

// ── Root panel with sub-navigation ─────────────────────────────────────────────
const TalentAdminPanel = ({ token }) => {
  const [sub, setSub] = useState("campaigns"); // campaigns | applications | templates
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);

  const tabs = [
    { id: "campaigns", label: "Campaigns", icon: Layers },
    { id: "applications", label: "Applications", icon: Users },
    { id: "templates", label: "Templates", icon: FileText },
  ];

  return (
    <div className="space-y-5" data-testid="talent-panel">
      <div className="flex items-center gap-2 border-b border-[rgba(255,255,255,0.06)] pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`talent-tab-${t.id}`}
            onClick={() => { setSub(t.id); setSelectedCampaign(null); setSelectedApp(null); }}
            className={`mono-font text-xs uppercase tracking-wider px-3 py-2 rounded-md flex items-center gap-2 transition-colors ${
              sub === t.id
                ? "text-[#17B352] bg-[rgba(23,179,82,0.08)]"
                : "text-[#52525B] hover:text-[#A1A1AA]"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {sub === "campaigns" && !selectedCampaign && (
        <CampaignsList token={token} onOpen={setSelectedCampaign} />
      )}
      {sub === "campaigns" && selectedCampaign && (
        <CampaignDetail
          token={token}
          campaignId={selectedCampaign}
          onBack={() => setSelectedCampaign(null)}
        />
      )}
      {sub === "applications" && !selectedApp && (
        <ApplicationsList token={token} onOpen={setSelectedApp} />
      )}
      {sub === "applications" && selectedApp && (
        <ApplicationDetail token={token} appId={selectedApp} onBack={() => setSelectedApp(null)} />
      )}
      {sub === "templates" && <TemplatesList token={token} />}
    </div>
  );
};

// ── Campaigns ──────────────────────────────────────────────────────────────────
const CampaignsList = ({ token, onOpen }) => {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cData }, { data: tData }] = await Promise.all([
        axios.get(`${API}/admin/talent/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/talent/templates`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setItems(cData.items || []);
      setTemplates(tData.items || []);
    } catch (e) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl">Hiring Campaigns</h3>
        <button
          data-testid="talent-create-campaign-btn"
          onClick={() => setShowCreate(true)}
          className="glow-button flex items-center gap-2 text-sm py-2 px-5"
        >
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {loading ? (
        <div className="glass-card rounded-xl p-8 text-center mono-font text-[#52525B]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center">
          <p className="text-[#52525B] mono-font text-sm">
            No campaigns yet. Create one from the &ldquo;Subtitle Translator Application&rdquo; system template.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <button
              key={c.id}
              data-testid={`campaign-row-${c.id}`}
              onClick={() => onOpen(c.id)}
              className="glass-card rounded-xl p-5 w-full text-left hover:border-[#17B352]/40 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h4 className="text-lg">{c.internal_name}</h4>
                    <span className={`mono-font text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      c.status === "published" ? "text-[#32D74B] border-[#32D74B]/30"
                        : c.status === "closed" ? "text-[#FF375F] border-[#FF375F]/30"
                        : "text-[#17B352] border-[#17B352]/30"
                    }`}>{c.status}</span>
                  </div>
                  <div className="flex gap-4 mono-font text-xs text-[#52525B] mt-1">
                    <span>/{c.slug}</span>
                    <span>{c.application_count || 0} applications</span>
                    <span>Created {fmtDate(c.created_at)}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#52525B]" />
              </div>
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showCreate && (
          <CreateCampaignModal
            token={token}
            templates={templates}
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); fetchAll(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const CreateCampaignModal = ({ token, templates, onClose, onCreated }) => {
  const [form, setForm] = useState({
    template_id: templates[0]?.id || "",
    internal_name: "",
    public_title: "",
    slug: "",
    role: "Subtitle Translator",
    source_languages: "",
    target_languages: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form.template_id && templates[0]) {
      setForm((f) => ({ ...f, template_id: templates[0].id }));
    }
  }, [templates, form.template_id]);

  const update = (patch) => setForm((f) => {
    const next = { ...f, ...patch };
    if (patch.public_title !== undefined && !f.slug) next.slug = slugify(patch.public_title);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.template_id) return toast.error("Choose a template");
    if (!form.internal_name || !form.public_title || !form.slug) {
      return toast.error("Fill required fields");
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/talent/campaigns`, {
        template_id: form.template_id,
        internal_name: form.internal_name,
        public_title: form.public_title,
        slug: form.slug,
        role: form.role,
        source_languages: form.source_languages.split(",").map((s) => s.trim()).filter(Boolean),
        target_languages: form.target_languages.split(",").map((s) => s.trim()).filter(Boolean),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Campaign created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create campaign");
    } finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.form
        initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="glass-card rounded-2xl p-6 w-full max-w-lg space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg">Create Campaign</h3>
          <button type="button" onClick={onClose} className="text-[#52525B] hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <Field label="Template">
          <select
            data-testid="campaign-template-select"
            className="glass-input"
            value={form.template_id}
            onChange={(e) => update({ template_id: e.target.value })}
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Internal name">
          <input data-testid="campaign-internal-name" className="glass-input"
            value={form.internal_name}
            onChange={(e) => update({ internal_name: e.target.value })}
            placeholder="Tamil Subtitle Translators — Aug 2026" />
        </Field>

        <Field label="Public title">
          <input data-testid="campaign-public-title" className="glass-input"
            value={form.public_title}
            onChange={(e) => update({ public_title: e.target.value })}
            placeholder="Tamil Subtitle Translators" />
        </Field>

        <Field label="URL slug" help="Public link will be /apply/{slug}">
          <input data-testid="campaign-slug" className="glass-input"
            value={form.slug}
            onChange={(e) => update({ slug: slugify(e.target.value) })}
            placeholder="tamil-subtitle-translators" />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Source languages">
            <input className="glass-input" value={form.source_languages}
              onChange={(e) => update({ source_languages: e.target.value })}
              placeholder="English, Hindi" />
          </Field>
          <Field label="Target languages">
            <input className="glass-input" value={form.target_languages}
              onChange={(e) => update({ target_languages: e.target.value })}
              placeholder="Tamil" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="mono-font text-xs uppercase tracking-wider px-4 py-2 text-[#A1A1AA] hover:text-white">
            Cancel
          </button>
          <button data-testid="campaign-create-submit" disabled={saving} className="glow-button text-sm py-2 px-5">
            {saving ? "Creating…" : "Create Campaign"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
};

const CampaignDetail = ({ token, campaignId, onBack }) => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copyOk, setCopyOk] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/talent/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaign(data);
    } catch (e) {
      toast.error("Failed to load campaign");
    } finally { setLoading(false); }
  }, [campaignId, token]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const applyUrl = useMemo(() => {
    if (!campaign) return "";
    return `${window.location.origin}/apply/${campaign.slug}`;
  }, [campaign]);

  const persist = async (nextSchema) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/talent/campaigns/${campaignId}`,
        { form_schema: nextSchema },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCampaign((c) => ({ ...c, form_schema: nextSchema }));
    } catch (e) {
      toast.error("Failed to save changes");
    } finally { setSaving(false); }
  };

  const toggleSection = async (sectionKey) => {
    if (!campaign) return;
    const schema = JSON.parse(JSON.stringify(campaign.form_schema));
    for (const s of schema.sections) if (s.key === sectionKey) s.enabled = !s.enabled;
    await persist(schema);
  };

  const setFieldState = async (sectionKey, fieldKey, state) => {
    if (!campaign) return;
    const schema = JSON.parse(JSON.stringify(campaign.form_schema));
    for (const s of schema.sections) {
      if (s.key !== sectionKey) continue;
      for (const f of s.fields) if (f.key === fieldKey) f.state = state;
    }
    await persist(schema);
  };

  const publish = async () => {
    try {
      await axios.post(`${API}/admin/talent/campaigns/${campaignId}/publish`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Campaign published");
      fetchDetail();
    } catch (e) {
      toast.error("Failed to publish");
    }
  };

  const changeStatus = async (status) => {
    try {
      await axios.patch(`${API}/admin/talent/campaigns/${campaignId}`, { status },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status → ${status}`);
      fetchDetail();
    } catch (e) { toast.error("Failed"); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(applyUrl);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch { /* noop */ }
  };

  if (loading || !campaign) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="mono-font text-xs uppercase tracking-wider text-[#52525B] hover:text-white flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="glass-card rounded-xl p-8 text-center mono-font text-[#52525B]">Loading…</div>
      </div>
    );
  }

  const schema = campaign.form_schema || { sections: [] };

  return (
    <div className="space-y-5">
      <button
        data-testid="campaign-back-btn"
        onClick={onBack}
        className="mono-font text-xs uppercase tracking-wider text-[#52525B] hover:text-white flex items-center gap-1"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Campaigns
      </button>

      <div className="glass-card rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-xl">{campaign.internal_name}</h3>
              <span className={`mono-font text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                campaign.status === "published" ? "text-[#32D74B] border-[#32D74B]/30"
                  : campaign.status === "closed" ? "text-[#FF375F] border-[#FF375F]/30"
                  : "text-[#17B352] border-[#17B352]/30"
              }`}>{campaign.status}</span>
            </div>
            <p className="mono-font text-xs text-[#52525B] mt-1">{campaign.public_title}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {campaign.status !== "published" && (
              <button data-testid="campaign-publish-btn" onClick={publish} className="glow-button text-xs py-2 px-4 flex items-center gap-1">
                <Send className="w-3.5 h-3.5" /> Publish
              </button>
            )}
            {campaign.status === "published" && (
              <button onClick={() => changeStatus("paused")} className="mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-md border border-[#17B352]/30 text-[#17B352] hover:bg-[#17B352]/10">
                Pause
              </button>
            )}
            {campaign.status !== "closed" && (
              <button onClick={() => changeStatus("closed")} className="mono-font text-xs uppercase tracking-wider px-4 py-2 rounded-md border border-[#FF375F]/30 text-[#FF375F] hover:bg-[#FF375F]/10">
                Close
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 bg-[rgba(255,255,255,0.03)] rounded-lg p-3">
          <ExternalLink className="w-4 h-4 text-[#17B352]" />
          <a href={applyUrl} target="_blank" rel="noreferrer" className="mono-font text-xs text-[#A1A1AA] hover:text-[#17B352] truncate flex-1">
            {applyUrl}
          </a>
          <button data-testid="campaign-copy-link" onClick={copyLink} className="mono-font text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[#17B352]/30 text-[#17B352] hover:bg-[#17B352]/10 flex items-center gap-1">
            <Copy className="w-3 h-3" /> {copyOk ? "Copied" : "Copy"}
          </button>
        </div>

        <div className="mono-font text-xs text-[#52525B]">
          {campaign.application_count || 0} applications received
          {saving && <span className="text-[#17B352] ml-2">Saving…</span>}
        </div>
      </div>

      {/* Form schema editor */}
      <div className="space-y-3">
        <h4 className="text-base">Form Configuration</h4>
        <p className="mono-font text-xs text-[#52525B]">
          Toggle whole sections off or mark individual fields as Required, Optional, or Hidden.
          Changes save automatically and apply only to this campaign.
        </p>
        {schema.sections.map((s) => (
          <div key={s.key} className="glass-card rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h5 className="text-base">{s.title}</h5>
                <p className="mono-font text-[10px] uppercase tracking-wider text-[#52525B] mt-1">
                  {s.fields.filter((f) => f.state !== "hidden").length}/{s.fields.length} active fields
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="mono-font text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                  {s.enabled ? "On" : "Off"}
                </span>
                <button
                  data-testid={`section-toggle-${s.key}`}
                  onClick={() => toggleSection(s.key)}
                  className={`w-10 h-5 rounded-full relative transition-colors ${
                    s.enabled ? "bg-[#17B352]" : "bg-[#3F3F46]"
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
                    s.enabled ? "left-5" : "left-0.5"
                  }`} />
                </button>
              </label>
            </div>
            {s.enabled && (
              <div className="space-y-1.5">
                {s.fields.map((f) => (
                  <div key={f.key} className="flex items-center justify-between py-1.5 border-t border-[rgba(255,255,255,0.04)]">
                    <div className="min-w-0">
                      <p className="text-sm truncate">{f.label}</p>
                      <p className="mono-font text-[10px] uppercase tracking-wider text-[#52525B]">
                        {f.type} · {f.key}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {["required", "optional", "hidden"].map((st) => (
                        <button
                          key={st}
                          data-testid={`field-state-${s.key}-${f.key}-${st}`}
                          onClick={() => setFieldState(s.key, f.key, st)}
                          className={`mono-font text-[10px] uppercase tracking-wider px-2 py-1 rounded transition-colors ${
                            f.state === st
                              ? st === "required"
                                ? "bg-[#17B352] text-black"
                                : st === "optional"
                                ? "bg-[#32D74B]/20 text-[#32D74B] border border-[#32D74B]/30"
                                : "bg-[#3F3F46] text-[#A1A1AA] border border-[#52525B]"
                              : "text-[#52525B] hover:text-white border border-transparent"
                          }`}
                        >
                          {st === "hidden" ? <EyeOff className="w-3 h-3 inline" /> : st === "optional" ? <Eye className="w-3 h-3 inline" /> : null}
                          {" "}{st}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Applications ───────────────────────────────────────────────────────────────
const ApplicationsList = ({ token, onOpen }) => {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filter, setFilter] = useState({ campaign_id: "", status: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: cData }] = await Promise.all([
        axios.get(`${API}/admin/talent/applications`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { campaign_id: filter.campaign_id || undefined, status: filter.status || undefined },
        }),
        axios.get(`${API}/admin/talent/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setItems(data.items || []);
      setCampaigns(cData.items || []);
    } catch (e) {
      toast.error("Failed to load applications");
    } finally { setLoading(false); }
  }, [filter, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4">
      <h3 className="text-xl">Applications</h3>

      <div className="flex gap-2 flex-wrap">
        <select className="glass-input max-w-xs"
          data-testid="applications-filter-campaign"
          value={filter.campaign_id}
          onChange={(e) => setFilter((f) => ({ ...f, campaign_id: e.target.value }))}
        >
          <option value="">All campaigns</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.internal_name}</option>)}
        </select>
        <select className="glass-input max-w-xs"
          data-testid="applications-filter-status"
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="">All statuses</option>
          {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_LABEL[k]}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="glass-card rounded-xl p-8 text-center mono-font text-[#52525B]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center">
          <p className="text-[#52525B] mono-font text-sm">No applications yet.</p>
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="mono-font text-[10px] uppercase tracking-wider text-[#52525B] border-b border-[rgba(255,255,255,0.06)]">
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Applicant</th>
                <th className="text-left p-3">Campaign</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id}
                  data-testid={`app-row-${a.id}`}
                  onClick={() => onOpen(a.id)}
                  className="border-t border-[rgba(255,255,255,0.04)] hover:bg-[rgba(23,179,82,0.05)] cursor-pointer"
                >
                  <td className="p-3 mono-font text-xs text-[#17B352]">{a.application_code}</td>
                  <td className="p-3">
                    <div>{a.applicant?.full_name || "—"}</div>
                    <div className="mono-font text-[10px] text-[#52525B]">{a.applicant?.email}</div>
                  </td>
                  <td className="p-3 text-[#A1A1AA]">{a.campaign?.internal_name || a.campaign?.public_title || "—"}</td>
                  <td className="p-3">
                    <span className="mono-font text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#17B352]/30 text-[#17B352]">
                      {STATUS_LABEL[a.status] || a.status}
                    </span>
                  </td>
                  <td className="p-3 mono-font text-xs text-[#52525B]">{fmtDate(a.submitted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ApplicationDetail = ({ token, appId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchIt = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/talent/applications/${appId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) { toast.error("Failed to load application"); }
    finally { setLoading(false); }
  }, [appId, token]);

  useEffect(() => { fetchIt(); }, [fetchIt]);

  const changeStatus = async (status) => {
    try {
      await axios.patch(`${API}/admin/talent/applications/${appId}`, { status },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Status updated");
      fetchIt();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="mono-font text-xs uppercase tracking-wider text-[#52525B] hover:text-white flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="glass-card rounded-xl p-8 text-center mono-font text-[#52525B]">Loading…</div>
      </div>
    );
  }

  const answers = data.answers || {};

  return (
    <div className="space-y-5">
      <button data-testid="app-back-btn" onClick={onBack} className="mono-font text-xs uppercase tracking-wider text-[#52525B] hover:text-white flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Applications
      </button>

      <div className="glass-card rounded-xl p-6 space-y-3">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-xl">{data.applicant?.full_name || "Applicant"}</h3>
              <span className="mono-font text-xs text-[#17B352]">{data.application_code}</span>
            </div>
            <div className="flex flex-wrap gap-4 mono-font text-xs text-[#52525B] mt-1">
              <span>{data.applicant?.primary_email}</span>
              <span>{data.applicant?.primary_phone}</span>
              <span>{data.applicant?.country}</span>
              <span>{data.campaign?.public_title}</span>
              <span>Submitted {fmtDate(data.submitted_at)}</span>
            </div>
          </div>

          <select
            data-testid="app-status-select"
            className="glass-input max-w-xs"
            value={data.status}
            onChange={(e) => changeStatus(e.target.value)}
          >
            {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_LABEL[k]}</option>)}
          </select>
        </div>
      </div>

      {(data.files || []).length > 0 && (
        <div className="glass-card rounded-xl p-5 space-y-2">
          <h4 className="text-base mb-2">Uploaded files</h4>
          {data.files.map((f) => (
            <a key={f.id} href={f.url} target="_blank" rel="noreferrer"
              className="flex items-center justify-between border-t border-[rgba(255,255,255,0.04)] py-2 hover:text-[#17B352]">
              <span>
                <span className="mono-font text-[10px] uppercase tracking-wider text-[#52525B] mr-2">{f.field_key}</span>
                {f.filename}
              </span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ))}
        </div>
      )}

      <div className="glass-card rounded-xl p-5 space-y-3">
        <h4 className="text-base mb-2">Answers</h4>
        {Object.entries(answers).length === 0 && <p className="mono-font text-xs text-[#52525B]">No answers.</p>}
        {Object.entries(answers).map(([k, v]) => (
          <div key={k} className="border-t border-[rgba(255,255,255,0.04)] pt-2">
            <p className="mono-font text-[10px] uppercase tracking-wider text-[#52525B]">{k}</p>
            <p className="text-sm break-words">
              {typeof v === "object" ? (v?.filename ? v.filename : JSON.stringify(v)) : String(v)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Templates (read-only in Phase 1) ───────────────────────────────────────────
const TemplatesList = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/talent/templates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setItems(data.items || []);
      } catch (e) { toast.error("Failed to load templates"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  return (
    <div className="space-y-4">
      <h3 className="text-xl">Templates</h3>
      <p className="mono-font text-xs text-[#52525B]">
        System templates are the master forms. Create a campaign from a template to customise it
        (drag/drop builder ships in Phase 3).
      </p>
      {loading ? (
        <div className="glass-card rounded-xl p-8 text-center mono-font text-[#52525B]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((t) => (
            <div key={t.id} className="glass-card rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="text-base">{t.name}</h4>
                <span className="mono-font text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#17B352]/30 text-[#17B352]">
                  {t.template_type}
                </span>
              </div>
              <p className="mono-font text-xs text-[#52525B] mb-2">{t.description}</p>
              <div className="flex justify-between mono-font text-[10px] uppercase tracking-wider text-[#52525B]">
                <span>{t.category}</span>
                <span>{t.campaign_count || 0} campaigns</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const Field = ({ label, help, children }) => (
  <div>
    <label className="mono-font text-[10px] uppercase tracking-wider text-[#52525B] block mb-1">{label}</label>
    {children}
    {help && <p className="mono-font text-[10px] text-[#52525B] mt-1">{help}</p>}
  </div>
);

export default TalentAdminPanel;
